import { z } from 'zod';
import type { NoteColor } from '../colors.js';
import { ALLOWED_ATTACHMENT_MIME_TYPES, LIMITS } from '../limits.js';
import type { ImportNoteInput } from '../schemas/import.js';

/**
 * Lecture d'un export Google Takeout de Keep.
 *
 * Takeout produit un fichier JSON par note (plus un .html qu'on ignore), les pièces jointes à côté,
 * et un `Labels.txt`. Il n'y a pas d'identifiant de note : on utilise l'horodatage de création,
 * stable même si la note est renommée.
 */

/** Couleurs historiques de Keep → palette actuelle (Keep les a renommées en 2023). */
export const KEEP_COLOR_MAP: Record<string, NoteColor> = {
  DEFAULT: 'default',
  RED: 'coral',
  ORANGE: 'peach',
  YELLOW: 'sand',
  GREEN: 'mint',
  TEAL: 'sage',
  BLUE: 'fog',
  CERULEAN: 'storm',
  DARK_BLUE: 'storm',
  PURPLE: 'dusk',
  PINK: 'blossom',
  BROWN: 'clay',
  GRAY: 'chalk',
  GREY: 'chalk',
};

const timestamp = z.union([z.number(), z.string()]).optional();

const takeoutNoteSchema = z.object({
  title: z.string().optional(),
  textContent: z.string().optional(),
  listContent: z
    .array(z.object({ text: z.string().optional(), isChecked: z.boolean().optional() }))
    .optional(),
  color: z.string().optional(),
  isPinned: z.boolean().optional(),
  isArchived: z.boolean().optional(),
  isTrashed: z.boolean().optional(),
  labels: z.array(z.object({ name: z.string() })).optional(),
  attachments: z
    .array(z.object({ filePath: z.string(), mimetype: z.string().optional() }))
    .optional(),
  annotations: z.array(z.object({ url: z.string().optional() })).optional(),
  sharees: z.array(z.unknown()).optional(),
  createdTimestampUsec: timestamp,
  userEditedTimestampUsec: timestamp,
});

export interface KeepAttachmentRef {
  /** Chemin tel qu'écrit dans le JSON, relatif au dossier de la note. */
  filePath: string;
  mimeType: string | null;
}

export interface ParsedKeepNote {
  /** Note prête pour l'API d'import (les pièces jointes sont envoyées à part). */
  note: Required<
    Pick<
      ImportNoteInput,
      | 'externalId'
      | 'title'
      | 'content'
      | 'type'
      | 'items'
      | 'color'
      | 'labels'
      | 'pinned'
      | 'archived'
      | 'trashed'
    >
  > &
    Pick<ImportNoteInput, 'createdAt' | 'updatedAt'>;
  attachments: KeepAttachmentRef[];
  /** Informations perdues ou modifiées pendant la conversion, à montrer à l'utilisateur. */
  warnings: string[];
}

/** Reconnaît un JSON de note Keep parmi les fichiers d'une archive Takeout (qui peut contenir d'autres produits). */
export function isKeepTakeoutNote(json: unknown): boolean {
  if (typeof json !== 'object' || json === null || Array.isArray(json)) return false;
  const hasTimestamp = 'userEditedTimestampUsec' in json || 'createdTimestampUsec' in json;
  const hasBody = 'textContent' in json || 'listContent' in json || 'title' in json;
  return hasTimestamp && hasBody;
}

function usecToIso(value: number | string | undefined): string | undefined {
  if (value === undefined) return undefined;
  const ms = Math.floor(Number(value) / 1000);
  if (!Number.isFinite(ms) || ms <= 0) return undefined;
  return new Date(ms).toISOString();
}

function truncate(text: string, max: number, what: string, warnings: string[]): string {
  if (text.length <= max) return text;
  warnings.push(`${what} tronqué à ${max} caractères`);
  return text.slice(0, max);
}

const SUPPORTED_MIME = new Set<string>(ALLOWED_ATTACHMENT_MIME_TYPES);
const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp)$/i;

/**
 * Convertit le JSON d'une note Keep. Renvoie `null` si le fichier n'est pas une note
 * ou si la note est vide (Keep en conserve parfois).
 */
export function parseKeepNote(json: unknown, fileName: string): ParsedKeepNote | null {
  if (!isKeepTakeoutNote(json)) return null;
  const parsed = takeoutNoteSchema.safeParse(json);
  if (!parsed.success) return null;
  const raw = parsed.data;
  const warnings: string[] = [];

  const title = truncate(
    (raw.title ?? '').replace(/\r?\n/g, ' ').trim(),
    LIMITS.noteTitle,
    'Titre',
    warnings,
  );

  let content = raw.textContent ?? '';
  // Liens détectés par Keep (aperçus) : ajoutés au texte s'ils n'y figurent pas déjà.
  const links = (raw.annotations ?? [])
    .map((a) => a.url?.trim())
    .filter((url): url is string => Boolean(url) && !content.includes(url!));
  if (links.length) content = [content.trimEnd(), ...links].filter(Boolean).join('\n');
  content = truncate(content, LIMITS.noteContent, 'Texte', warnings);

  let items = (raw.listContent ?? [])
    .map((item) => ({
      text: (item.text ?? '').replace(/\r?\n/g, ' '),
      checked: item.isChecked ?? false,
    }))
    .filter((item) => item.text.trim() !== '');
  if (items.length > LIMITS.checklistItems) {
    warnings.push(`Liste limitée à ${LIMITS.checklistItems} éléments`);
    items = items.slice(0, LIMITS.checklistItems);
  }
  if (items.some((item) => item.text.length > LIMITS.checklistItemText)) {
    warnings.push(`Éléments de liste tronqués à ${LIMITS.checklistItemText} caractères`);
    items = items.map((item) => ({ ...item, text: item.text.slice(0, LIMITS.checklistItemText) }));
  }

  const attachments: KeepAttachmentRef[] = [];
  let unsupported = 0;
  for (const attachment of raw.attachments ?? []) {
    const mimeType = attachment.mimetype?.toLowerCase() ?? null;
    const isImage = mimeType
      ? SUPPORTED_MIME.has(mimeType)
      : IMAGE_EXTENSIONS.test(attachment.filePath);
    if (isImage) attachments.push({ filePath: attachment.filePath, mimeType });
    else unsupported++;
  }
  if (unsupported) {
    warnings.push(
      unsupported === 1
        ? '1 pièce jointe non prise en charge (enregistrement audio…) ignorée'
        : `${unsupported} pièces jointes non prises en charge (enregistrements audio…) ignorées`,
    );
  }

  if (!title && !content.trim() && items.length === 0 && attachments.length === 0) return null;

  const colorKey = (raw.color ?? 'DEFAULT').toUpperCase();
  const color = KEEP_COLOR_MAP[colorKey] ?? 'default';
  if (!(colorKey in KEEP_COLOR_MAP))
    warnings.push(`Couleur inconnue « ${raw.color} » remplacée par la couleur par défaut`);

  // Dédoublonnage insensible à la casse, en gardant la première graphie rencontrée.
  const labels: string[] = [];
  for (const label of raw.labels ?? []) {
    const name = label.name.trim().slice(0, LIMITS.labelName);
    if (name && !labels.some((l) => l.toLocaleLowerCase() === name.toLocaleLowerCase()))
      labels.push(name);
  }

  if ((raw.sharees ?? []).length > 0) warnings.push('Note partagée : le partage n’est pas importé');

  // Une liste Keep n'a pas de texte libre : le contenu ne sert qu'aux notes texte.
  const type = raw.listContent !== undefined && items.length > 0 ? 'checklist' : 'text';
  const createdAt = usecToIso(raw.createdTimestampUsec);
  const updatedAt = usecToIso(raw.userEditedTimestampUsec) ?? createdAt;
  const archived = raw.isArchived ?? false;

  return {
    note: {
      externalId:
        raw.createdTimestampUsec !== undefined
          ? `keep:${raw.createdTimestampUsec}`
          : `keep-file:${fileName}`,
      title,
      content: type === 'text' ? content : '',
      type,
      items,
      color,
      labels,
      // Keep n'autorise pas une note à la fois archivée et épinglée : l'archive l'emporte.
      pinned: (raw.isPinned ?? false) && !archived,
      archived,
      trashed: raw.isTrashed ?? false,
      createdAt,
      updatedAt,
    },
    attachments,
    warnings,
  };
}

/** `Labels.txt` : un libellé par ligne. */
export function parseKeepLabelsFile(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim().slice(0, LIMITS.labelName))
    .filter(Boolean);
}

const stripExtension = (path: string) => path.replace(/\.[^./]+$/, '');

/**
 * Retrouve le fichier d'une pièce jointe dans le dossier de la note.
 * Takeout confond parfois les extensions (`.jpg` annoncé, `.jpeg` exporté) : on compare alors sans extension.
 *
 * @param available chemins des fichiers du dossier, relatifs à ce dossier
 */
export function resolveAttachmentPath(
  filePath: string,
  available: Iterable<string>,
): string | null {
  const files = [...available];
  const exact = files.find((file) => file === filePath);
  if (exact) return exact;
  const lower = filePath.toLowerCase();
  const caseInsensitive = files.find((file) => file.toLowerCase() === lower);
  if (caseInsensitive) return caseInsensitive;
  const base = stripExtension(lower);
  return files.find((file) => stripExtension(file.toLowerCase()) === base) ?? null;
}
